// src/services/api/PerfilApi.ts
import api from '../../utils/axios';
import {
  PerfilCompleto,
  UpdateContactoDto,
  ChangePasswordDto,
  UpdatePhotoDto,
} from '../../components/types/types/perfil.types';

export class PerfilApi {
  static async getProfile(id: number): Promise<PerfilCompleto> {
    try {
      const response = await api.get(`/users/profile/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Error al obtener el perfil del usuario'
      );
    }
  }

  static async updatePhoto(id: number, imagen: string): Promise<void> {
    try {
      const payload: UpdatePhotoDto = { imagen };
      await api.post(`/users/update-photo/${id}`, payload);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Error al actualizar la foto de perfil'
      );
    }
  }

  static async changePassword(
    id: number,
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      const payload: ChangePasswordDto = { currentPassword, newPassword };
      await api.post(`/users/change-password/${id}`, payload);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Error al cambiar la contraseña'
      );
    }
  }

  static async updateContacto(
    idEmpleado: number,
    data: UpdateContactoDto
  ): Promise<void> {
    try {
      await api.post(`/empleados/update/${idEmpleado}`, data);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Error al actualizar los datos de contacto'
      );
    }
  }

  /**
   * Validar formato de email
   * @param email - Email a validar
   * @returns true si el email es válido
   */
  static validateEmail(email: string): boolean {
    if (!email || email.trim() === '') {
      return false;
    }

    // Regex para validar formato de email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Validar formato de teléfono
   * @param phone - Teléfono a validar
   * @returns true si el teléfono es válido
   */
  static validatePhone(phone: string): boolean {
    if (!phone || phone.trim() === '') {
      return false;
    }

    // Acepta números con espacios, guiones, paréntesis y símbolo +
    // Mínimo 7 dígitos, máximo 20 caracteres
    const phoneRegex = /^[\d\s\-\(\)\+]{7,20}$/;
    const cleanPhone = phone.trim();

    // Verificar que tenga al menos 7 dígitos
    const digitsOnly = cleanPhone.replace(/\D/g, '');
    if (digitsOnly.length < 7) {
      return false;
    }

    return phoneRegex.test(cleanPhone);
  }

  /**
   * Validar contraseña segura
   * @param password - Contraseña a validar
   * @returns Objeto con resultado de validación y mensaje
   */
  static validatePassword(password: string): {
    isValid: boolean;
    message: string;
  } {
    if (!password || password.trim() === '') {
      return {
        isValid: false,
        message: 'La contraseña no puede estar vacía',
      };
    }

    if (password.length < 6) {
      return {
        isValid: false,
        message: 'La contraseña debe tener al menos 6 caracteres',
      };
    }

    if (password.length > 50) {
      return {
        isValid: false,
        message: 'La contraseña no puede tener más de 50 caracteres',
      };
    }

    // Validar que contenga al menos un número
    const hasNumber = /\d/.test(password);
    if (!hasNumber) {
      return {
        isValid: false,
        message: 'La contraseña debe contener al menos un número',
      };
    }

    // Validar que contenga al menos una letra
    const hasLetter = /[a-zA-Z]/.test(password);
    if (!hasLetter) {
      return {
        isValid: false,
        message: 'La contraseña debe contener al menos una letra',
      };
    }

    // Validar que no tenga espacios
    if (/\s/.test(password)) {
      return {
        isValid: false,
        message: 'La contraseña no puede contener espacios',
      };
    }

    return {
      isValid: true,
      message: 'Contraseña válida',
    };
  }

  /**
   * Validar que las contraseñas coincidan
   * @param password - Contraseña
   * @param confirmPassword - Confirmación de contraseña
   * @returns true si coinciden
   */
  static validatePasswordMatch(password: string, confirmPassword: string): boolean {
    return password === confirmPassword && password.length > 0;
  }

  /**
   * Validar tamaño de imagen
   * @param file - Archivo de imagen
   * @param maxSizeMB - Tamaño máximo en MB (por defecto 5MB)
   * @returns true si el tamaño es válido
   */
  static validateImageSize(file: File, maxSizeMB: number = 5): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * Validar tipo de imagen
   * @param file - Archivo de imagen
   * @returns true si es un tipo de imagen válido
   */
  static validateImageType(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    return validTypes.includes(file.type);
  }

  /**
   * Convertir archivo a base64
   * @param file - Archivo a convertir
   * @returns Promise con string base64
   */
  static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Obtener extensión del archivo
   * @param filename - Nombre del archivo
   * @returns Extensión del archivo
   */
  static getFileExtension(filename: string): string {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
  }

  /**
   * Formatear tamaño de archivo
   * @param bytes - Tamaño en bytes
   * @returns Tamaño formateado (ej: "2.5 MB")
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
}